// config.js - Centralized configuration for all API URLs and settings

const CONFIG = {
  // =========================
  // API Configuration
  // =========================
  API: {
    BASE_URL: process.env.REACT_APP_API_URL || 'https://nirafinhub.niraitsolutions.com',
    API_V1_BASE_URL:
      process.env.REACT_APP_API_URL || 'https://nirafinhub.niraitsolutions.com/api/v1',

    // -------------------------
    // Auth Endpoints
    // -------------------------
    AUTH: {
      LOGIN: '/auth/login',
      REGISTER: '/auth/register',
      REFRESH: '/auth/refresh',
      LOGOUT: '/auth/logout',
      ME: '/auth/me'
    },

    // -------------------------
    // Admin Endpoints
    // -------------------------
    ADMIN: {
      USERS: {
        CREATE: '/admin/users',
        LIST: '/admin/users',
        DETAIL: (id) => `/admin/users/${id}`,
        UPDATE: (id) => `/admin/users/${id}`,
        DELETE: (id) => `/admin/users/${id}`,
        ACTIVATE: (id) => `/admin/users/${id}/activate`,
        DEACTIVATE: (id) => `/admin/users/${id}/deactivate`,
        RESET_PASSWORD: (id) => `/admin/users/${id}/reset-password`
      },

      COMPANIES: {
        CREATE: '/admin/companies',
        LIST: '/admin/companies',
        DETAIL: (id) => `/admin/companies/${id}`,
        UPDATE: (id) => `/admin/companies/${id}`,
        DELETE: (id) => `/admin/companies/${id}`,
        ASSIGN_TO_ADMIN: (id) => `/admin/companies/${id}/assign-admin`,
        ASSIGN_TO_USER: (id) => `/admin/companies/${id}/assign-user`
      },

      REPORTS: {
        USERS_OVERVIEW: '/admin/reports/users-overview',
        COMPANIES_OVERVIEW: '/admin/reports/companies-overview',
        SYSTEM_STATUS: '/admin/reports/system-status',
        EXTRACTION_STATS: '/admin/reports/extraction-stats',
        RECENT_EXTRACTIONS: '/admin/reports/recent-extractions'
      }
    },

    // -------------------------
    // User Endpoints
    // -------------------------
    USERS: {
      BASE: '/users',
      ME: '/users/me',
      PROFILE: '/users/profile',
      UPDATE_PROFILE: '/users/profile',
      CHANGE_PASSWORD: '/users/change-password',
      MANAGED_USERS: '/users/my-managed-users', // For current user
      MANAGED_USERS_BY_ID: (id) => `/users/${id}/managed-users`, // For specific admin
      USER_COMPANIES: (id) => `/users/${id}/companies`,
      ADMIN_COMPANIES: (id) => `/users/${id}/admin-companies`,
      MAP_TO_ADMIN: '/users/map-to-admin'
    },

    // -------------------------
    // Company Endpoints
    // -------------------------
    COMPANIES: {
      BASE: '/companies',
      MY_MANAGED_COMPANIES: '/companies/my-managed-companies',
      SUGGESTIONS: '/companies/suggestions',
      MAP_TO_USER: '/companies/map-to-user',
      MAP_TO_ADMIN: '/companies/map-to-admin'
    },

    // -------------------------
    // Jobs
    // -------------------------
    JOBS: {
      TODAY: '/jobs/today',
      STATUS: '/jobs/status'
    },

    // -------------------------
    // S3 Control (API V1)
    // -------------------------
    S3_CONTROL: {
      STATUS: '/api/v1/s3-control/status',
      START: '/api/v1/s3-control/start',
      STOP: '/api/v1/s3-control/stop',
      SET_DAILY_LIMIT: '/api/v1/s3-control/set-daily-limit',
      RESET_DAILY_COUNTS: '/api/v1/s3-control/reset-daily-counts',
      TRIGGER_PROCESSING: '/s3-control/trigger-processing'
    },

    // -------------------------
    // S3 Upload (ROOT)
    // -------------------------
    S3_UPLOAD: {
      UPLOAD_PDF: '/s3-upload/upload-pdf',
      UPLOAD_MULTIPLE: '/s3-upload/upload-multiple',
      LIST_FILES: '/s3-upload/list-company-files',
      TEST_CONNECTION: '/s3-upload/test-connection'
    },

    // -------------------------
    // Excel
    // -------------------------
    EXCEL: {
      SEARCH: '/excel/search',
      STATEMENT_DATA: (id) => `/excel/statement/${id}/data`,
      STATEMENT_DOWNLOAD: (id) => `/excel/statement/${id}/download`,
      EDIT_CELL: (id) => `/excel/edit-cell/${id}`,
      SAVE_CHANGES: (id) => `/excel/statement/${id}/save-changes`,
      STRUCTURE: (id) => `/excel/structure/${id}`,
      UPLOAD_UPDATE: (id) => `/excel/upload-update/${id}`,
      VALIDATE: (id) => `/excel/validate/${id}`
    },

    // -------------------------
    // Upload
    // -------------------------
    UPLOAD: {
      SIMPLE: '/upload/simple',
      JOB_POLL: (jobId) => `/upload/jobs/${jobId}/poll`,
      JOB_DOWNLOAD: (jobId, statementId) =>
        `/upload/jobs/${jobId}/download/${statementId}`
    }
  },

  // =========================
  // App Configuration
  // =========================
  APP: {
    NAME: 'Financial Data Platform',
    VERSION: '3.2.0',
    DEFAULT_PAGE_SIZE: 20,
    MAX_FILE_SIZE: 50 * 1024 * 1024,
    ALLOWED_FILE_TYPES: ['.pdf', '.xlsx', '.xls', '.csv']
  },

  // =========================
  // 🔁 BACKWARD COMPATIBILITY
  // =========================
  getFullUrl(endpoint, useApiV1 = true) {
    if (typeof endpoint === 'function') return endpoint;
    const base = useApiV1
      ? this.API.API_V1_BASE_URL
      : this.API.BASE_URL;
    return `${base}${endpoint}`;
  },

  // =========================
  // Universal endpoint helper
  // Handles strings or functions with args
  // =========================
  getEndpoint(endpoint, useApiV1 = true, ...args) {
    if (typeof endpoint === 'function') {
      const ep = endpoint(...args);
      return this.getFullUrl(ep, useApiV1);
    }
    return this.getFullUrl(endpoint, useApiV1);
  },

  // =========================
  // New Helpers (Recommended)
  // =========================
  getApiUrl(endpoint) {
    return `${this.API.API_V1_BASE_URL}${endpoint}`;
  },

  getRootUrl(endpoint) {
    return `${this.API.BASE_URL}${endpoint}`;
  },

  // =========================
  // Auth Helpers
  // =========================
  getToken() {
    return (
      localStorage.getItem('access_token') ||
      sessionStorage.getItem('access_token')
    );
  },

  getAuthHeaders(extra = {}) {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...extra
    };
  },

  getAuthHeadersForFiles(extra = {}) {
    const token = this.getToken();
    return {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...extra
    };
  },

  // =========================
  // Health Check
  // =========================
  async checkBackendAvailability() {
    try {
      const res = await fetch(`${this.API.BASE_URL}/health`);
      return res.ok;
    } catch {
      return false;
    }
  }
};

export default CONFIG;